import { type SubscribePayload, createClient, type Client, type Sink } from 'graphql-ws';
import { fg } from '@atlaskit/platform-feature-flags';
import { type SubscriptionCallback } from '../useSubscribeRealtimeChannel';

export type Subscription = {
	addCallback(callback: SubscriptionCallback): void;
	closed: boolean;
	onError(error: Error): void;
	onNext(payload?: string): void;
	removeCallback(callback: SubscriptionCallback): void;
	subscriptionCallbacks: SubscriptionCallback[];
	unsubscribe(): void;
};

const getAGGWebsocketURI = (): string => {
	if (process.env.NODE_ENV !== 'production') {
		// In dev mode this allows the local dev proxy to use the WebSocket host
		// that matches the backend origin host. The __backendOriginHost is only
		// available with local dev server.
		const host = (window as any).__backendOriginHost || window.location.host;

		return `wss://${host}/gateway/api/graphql/subscriptions`;
	}
	return `wss://${window.location.host}/gateway/api/graphql/subscriptions`;
};

const createAGGSubscriptionClient = (): Client => {
	return createClient({
		url: getAGGWebsocketURI(),
		retryAttempts: 2,
		lazy: true,
	});
};

const getAGGSubscriptionClient = (): Client => {
	let client = (window as any).__FORGE_AGG_SUBSCRIPTION_CLIENT__;
	if (!client) {
		client = createAGGSubscriptionClient();
		(window as any).__FORGE_AGG_SUBSCRIPTION_CLIENT__ = client;
	}
	return client;
};

class BasicSubscription implements Subscription {
	public closed: boolean;
	private baseUnsubscribe: () => void;
	private _subscriptionCallbacks: SubscriptionCallback[];

	constructor(baseUnsubscribe: () => void, callback: SubscriptionCallback) {
		this.closed = false;
		this.baseUnsubscribe = baseUnsubscribe;
		this._subscriptionCallbacks = [callback];
	}

	get subscriptionCallbacks() {
		return this._subscriptionCallbacks;
	}

	set subscriptionCallbacks(callbacks: SubscriptionCallback[]) {
		this._subscriptionCallbacks = callbacks;
	}

	public unsubscribe() {
		if (!this.closed) {
			this.baseUnsubscribe();
			this.closed = true;
			this._subscriptionCallbacks = [];
		}
	}

	public addCallback(callback: SubscriptionCallback) {
		this._subscriptionCallbacks.push(callback);
	}

	public removeCallback(callback: SubscriptionCallback) {
		const index = this._subscriptionCallbacks.indexOf(callback);
		if (index !== -1) {
			this._subscriptionCallbacks.splice(index, 1);
		}
	}

	public onNext(event: string) {
		const payload = fg('forge-ui-allow-realtime-object-payloads')
			? this.parsePayload(event)
			: event;
		this._subscriptionCallbacks.forEach((callback) => {
			try {
				callback(payload);
			} catch (error) {
				// eslint-disable-next-line no-console
				console.error('[forge-realtime] Error in subscription callback: ', error);
			}
		});
	}

	public onError(error: Error) {
		this._subscriptionCallbacks.forEach((callback) => {
			try {
				callback(null, error);
			} catch (callbackError) {
				// eslint-disable-next-line no-console
				console.error('[forge-realtime] Error in subscription callback:', callbackError);
			}
		});
	}

	private parsePayload(payload: string) {
		try {
			return JSON.parse(payload);
			// eslint-disable-next-line no-unused-vars
		} catch (err) {
			return payload;
		}
	}
}

export const subscribe = (
	payload: SubscribePayload,
	sink: Sink,
	subscriptionCallback: SubscriptionCallback,
	client?: Client,
): Subscription => {
	if (!client) {
		client = getAGGSubscriptionClient();
	}
	const unsubscribe = client.subscribe(payload, sink);
	return new BasicSubscription(unsubscribe, subscriptionCallback);
};
